import { useParams, useNavigate } from "react-router-dom";
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  offMessage,
  offUserJoined,
  onMessage,
  onUserJoined,
  sendAnswer,
  sendIceCandidate,
  sendMsg,
  sendOffer,
  onOffer,
  onAnswer,
  onIceCandidate,
  offOffer,
  offAnswer,
  offIceCandidate,
  onJoinedRoom,
  offJoinedRoom,
  setEditorControl,
  onEditorControlChange,
  offEditorControlChange,
  endInterview,
  onInterviewEnded,
  offInterviewEnded,
} from "../sockets/socket";
import { useEffect, useRef, useState } from "react";
import { createWebRTCHandler } from "../sockets/webRTC";
import { useDispatch, useSelector } from "react-redux";
import {
  getSingleInterview,
  updateInterviewStatus,
} from "../feature/interview/interviewAPI";
import Loader from "../Components/loader";
import CodeEditor from "../Components/CodeEditor";
import InterviewerNotesCard from "../Components/InterviewerNotesCard";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Code2,
  MonitorUp,
  MonitorDown,
  PhoneOff,
  MessageCircle,
  X,
  Send,
  CheckCircle2,
  Users,
} from "lucide-react";

export default function InterviewRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [msg, setMsg] = useState("");
  const [allMsgs, setAllMsgs] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isRemoteConnected, setIsRemoteConnected] = useState(false);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRemoteScreenSharing, setIsRemoteScreenSharing] = useState(false);
  const [remoteCameraStream, setRemoteCameraStream] = useState(null);
  const [remoteUserName, setRemoteUserName] = useState("Remote User");
  const [editorControlledBy, setEditorControlledBy] = useState("candidate");

  const webrtcHandlerRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localScreenRef = useRef(null);
  const remoteScreenRef = useRef(null);
  const remoteCameraOverlayRef = useRef(null);

  const currentInterview = useSelector(
    (state) => state.interview.currentInterview.data,
  );
  const loading = useSelector(
    (state) => state.interview.currentInterview.loading,
  );
  const dispatch = useDispatch();

  const handleOnChange = (e) => {
    setMsg(e.target.value);
  };

  const handleInterviewStatus = (status, id) => {
    dispatch(updateInterviewStatus({ status, id }));
  };

  const toggleScreenShare = async () => {
    const result =
      await webrtcHandlerRef.current?.toggleScreenShare(isScreenSharing);

    if (result?.success) {
      if (result.stream) {
        setIsScreenSharing(true);
        setTimeout(() => {
          if (localScreenRef.current) {
            localScreenRef.current.srcObject = result.stream;
          }
        }, 100);
      } else {
        setIsScreenSharing(false);
        if (localScreenRef.current) {
          localScreenRef.current.srcObject = null;
        }
      }
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      setUserId(userObj._id);
      setUserRole(userObj.role);
    }

    dispatch(getSingleInterview(roomId));
  }, [dispatch, roomId]);

  useEffect(() => {
    document.title = `Interviewly - Interview Room ${roomId}`;
  }, [roomId]);

  useEffect(() => {
    if (currentInterview && userId) {
      if (currentInterview.interviewerId?._id === userId) {
        setRemoteUserName(currentInterview.candidateId?.name || "Remote User");
      } else if (currentInterview.candidateId?._id === userId) {
        setRemoteUserName(currentInterview.interviewerId?.name || "Remote User");
      }
    }
  }, [currentInterview, userId]);

  useEffect(() => {
    if (remoteCameraOverlayRef.current && remoteCameraStream) {
      remoteCameraOverlayRef.current.srcObject = remoteCameraStream;
      remoteCameraOverlayRef.current.play().catch((err) => {});
    }
  }, [remoteCameraStream, isRemoteScreenSharing]);

  const [localStreamReady, setLocalStreamReady] = useState(false);

  useEffect(() => {
    if (!currentInterview) return;

    webrtcHandlerRef.current = createWebRTCHandler(
      roomId,
      sendOffer,
      sendAnswer,
      sendIceCandidate,
    );

    const initStream = async () => {
      try {
        const stream = await webrtcHandlerRef.current.initialLocalStream();
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        setLocalStreamReady(true);
      } catch (error) {
        alert(
          "Failed to access camera/microphone. Please grant permissions and reload.",
        );
      }
    };

    initStream();

    return () => {
      webrtcHandlerRef.current?.cleanup();
      setLocalStreamReady(false);
    };
  }, [roomId, currentInterview]);

  useEffect(() => {
    if (!currentInterview) return;
    if (!localStreamReady) {
      return;
    }

    connectSocket();
    joinRoom(roomId);

    onJoinedRoom(({ shouldCreateOffer }) => {
      if (shouldCreateOffer) {
        setTimeout(() => {
          webrtcHandlerRef.current?.createOffer(
            (remoteStream) => {
              if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = remoteStream;
                remoteVideoRef.current.play().catch((err) => {});
                setIsRemoteConnected(true);
              }
              setRemoteCameraStream(remoteStream);
              if (remoteCameraOverlayRef.current) {
                remoteCameraOverlayRef.current.srcObject = remoteStream;
                remoteCameraOverlayRef.current.play().catch((err) => {});
              }
            },
            (remoteScreenStream) => {
              if (remoteScreenRef.current) {
                remoteScreenRef.current.srcObject = remoteScreenStream;
                remoteScreenRef.current.play().catch((err) => {});
                setIsRemoteScreenSharing(true);
              }
            },
          );
        }, 1000);
      }
    });
    onUserJoined((data) => {});

    onOffer(({ offer }) => {
      webrtcHandlerRef.current?.handleOffer(
        offer,
        (remoteStream) => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            remoteVideoRef.current.play().catch((err) => {});
            setIsRemoteConnected(true);
          }
          setRemoteCameraStream(remoteStream);
          if (remoteCameraOverlayRef.current) {
            remoteCameraOverlayRef.current.srcObject = remoteStream;
            remoteCameraOverlayRef.current.play().catch((err) => {});
          }
        },
        (remoteScreenStream) => {
          if (remoteScreenRef.current) {
            remoteScreenRef.current.srcObject = remoteScreenStream;
            remoteScreenRef.current.play().catch((err) => {});
            setIsRemoteScreenSharing(true);
          }
        },
      );
    });

    onAnswer(({ answer }) => {
      webrtcHandlerRef.current?.handleAnswer(answer);
    });

    onIceCandidate(({ candidate }) => {
      webrtcHandlerRef.current?.handleIceCandidate(candidate);
    });

    onMessage(({ msg, userId, time }) => {
      setAllMsgs((prevMsgs) => [...prevMsgs, { msg, userId, time }]);
    });

    onEditorControlChange(({ controlledBy }) => {
      setEditorControlledBy(controlledBy);
    });

    onInterviewEnded(({ msg, roomId: endedRoomId }) => {
      setInterviewCompleted(true);
    });

    return () => {
      offUserJoined();
      offMessage();
      offOffer();
      offAnswer();
      offIceCandidate();
      offJoinedRoom();
      offEditorControlChange();
      offInterviewEnded();
      disconnectSocket();
    };
  }, [roomId, currentInterview, localStreamReady]);

  const toggleVideo = () => {
    const enabled = webrtcHandlerRef.current?.toggleVideo();
    setIsVideoOn(enabled);
  };

  const toggleAudio = () => {
    const enabled = webrtcHandlerRef.current?.toggleAudio();
    setIsAudioOn(enabled);
  };

  const toggleEditorControl = () => {
    const newControl =
      editorControlledBy === "candidate" ? "interviewer" : "candidate";
    setEditorControlledBy(newControl);
    setEditorControl({ roomId, controlledBy: newControl });
  };

  const handleEndInterview = () => {
    const confirmEnd = window.confirm(
      "Are you sure you want to end the interview?",
    );
    if (confirmEnd) {
      endInterview({ roomId });
      handleInterviewStatus("completed", roomId);
      setInterviewCompleted(true);
    }
  };

  const cleanupAndLeave = () => {
    webrtcHandlerRef.current?.cleanup();
    offUserJoined();
    offMessage();
    offOffer();
    offAnswer();
    offIceCandidate();
    offJoinedRoom();
    offEditorControlChange();
    offInterviewEnded();
    disconnectSocket();
    navigate("/dashboard", { state: { interviewFinished: true } });
  };

  const sendCurrentMessage = () => {
    if (!userId || !msg.trim()) return;
    const time = Date.now();
    sendMsg({ roomId, msg, time });
    setAllMsgs((prevMsgs) => [...prevMsgs, { msg, userId, time }]);
    setMsg("");
  };

  const roomBadge = (
    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-medium text-emerald-300">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      Live session
    </span>
  );

  const controlButton = (active, activeCls, inactiveCls, onClick, label, children) => (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-200 ${
        active ? activeCls : inactiveCls
      }`}
    >
      {children}
    </button>
  );

  return loading ? (
    <Loader />
  ) : interviewCompleted ? (
    <div className="flex min-h-screen items-center justify-center bg-cocoa-800 px-4 py-16">
      <div className="w-full max-w-md animate-scale-in">
        <div className="card p-8 text-center shadow-modal sm:p-10">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-400/15 ring-1 ring-inset ring-emerald-400/30">
            <CheckCircle2 className="h-10 w-10 text-emerald-300" strokeWidth={2} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-cream-100">
            Interview Completed
          </h1>
          <p className="mt-3 text-amber-100/70">
            Thank you for participating. The interview has been successfully
            completed and saved.
          </p>
          <div className="mt-6 rounded-xl border border-amber-400/20 bg-amber-400/10 p-4">
            <p className="text-sm leading-relaxed text-amber-200">
              You can now safely close this page or return to your dashboard to
              view your interview history.
            </p>
          </div>
          <button onClick={cleanupAndLeave} className="btn btn-primary btn-lg mt-8 w-full">
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  ) : (
    <>
      <div className="min-h-screen bg-cocoa-950 p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300 ring-1 ring-inset ring-amber-400/20">
                <Video className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-lg font-semibold tracking-tight text-white sm:text-xl">
                  Interview Room
                </h1>
                <p className="font-mono text-xs text-amber-100/50">
                  Room ID: {roomId}
                </p>
              </div>
            </div>
            {roomBadge}
          </div>

          {/* Video Container */}
          <div className="mb-5">
            {expandedVideo ? (
              <div className="relative">
                <div
                  className="relative aspect-video cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-modal"
                  onClick={() => setExpandedVideo(null)}
                >
                  {expandedVideo === "remote" && !isRemoteConnected && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
                      <div className="text-center">
                        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
                        <p className="text-sm text-amber-200">
                          Waiting for {remoteUserName}...
                        </p>
                      </div>
                    </div>
                  )}
                  {expandedVideo === "remote" && isRemoteScreenSharing ? (
                    <>
                      <video
                        ref={remoteScreenRef}
                        autoPlay
                        playsInline
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute top-3 right-3 rounded-lg bg-purple-500/90 px-2.5 py-1 text-xs font-semibold text-white">
                        Screen sharing
                      </div>
                    </>
                  ) : (
                    <video
                      ref={
                        expandedVideo === "remote"
                          ? remoteVideoRef
                          : localVideoRef
                      }
                      autoPlay
                      playsInline
                      muted={expandedVideo === "local"}
                      className={`h-full w-full object-cover ${
                        expandedVideo === "local" ? "mirror" : ""
                      }`}
                    />
                  )}
                  <div className="absolute bottom-3 left-3 rounded-lg bg-black/60 px-3 py-1 text-sm text-white backdrop-blur-sm">
                    {expandedVideo === "remote"
                      ? isRemoteScreenSharing
                        ? `${remoteUserName}'s Screen`
                        : remoteUserName
                      : "You"}
                  </div>
                  {expandedVideo === "remote" &&
                    isRemoteConnected &&
                    !isRemoteScreenSharing && (
                      <div className="absolute top-3 right-3 rounded-lg bg-red-500/90 px-2.5 py-1 text-xs font-semibold text-white">
                        Live
                      </div>
                    )}
                  <div className="absolute top-3 left-3 rounded-lg bg-black/60 px-2.5 py-1 text-xs text-white/80 backdrop-blur-sm">
                    Click to minimize
                  </div>
                </div>

                <div
                  className="absolute right-4 bottom-4 w-48 cursor-pointer overflow-hidden rounded-xl border-2 border-white/20 bg-gray-800 shadow-modal transition-all hover:border-amber-400/60 md:w-64"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedVideo(
                      expandedVideo === "remote" ? "local" : "remote",
                    );
                  }}
                >
                  <video
                    ref={
                      expandedVideo === "remote" ? localVideoRef : remoteVideoRef
                    }
                    autoPlay
                    playsInline
                    muted={expandedVideo === "remote"}
                    className={`h-full w-full object-cover ${
                      expandedVideo === "remote" ? "mirror" : ""
                    }`}
                  />
                  <div className="absolute bottom-1.5 left-1.5 rounded-md bg-black/60 px-2 py-0.5 text-xs text-white backdrop-blur-sm">
                    {expandedVideo === "remote" ? "You" : remoteUserName}
                  </div>
                  {expandedVideo === "local" && (
                    <div className="absolute top-1.5 right-1.5 rounded-md bg-red-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      Live
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div
                  className="group relative aspect-video cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-modal transition-all hover:border-amber-400/40"
                  onClick={() => setExpandedVideo("remote")}
                >
                  {!isRemoteConnected && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
                      <div className="text-center">
                        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
                        <p className="text-sm text-amber-200">
                          Waiting for {remoteUserName}...
                        </p>
                      </div>
                    </div>
                  )}
                  {isRemoteScreenSharing ? (
                    <>
                      <video
                        ref={remoteScreenRef}
                        autoPlay
                        playsInline
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute top-3 right-3 z-10 rounded-lg bg-purple-500/90 px-2.5 py-1 text-xs font-semibold text-white">
                        Screen sharing
                      </div>
                    </>
                  ) : (
                    <video
                      ref={!isRemoteScreenSharing ? remoteVideoRef : null}
                      autoPlay
                      playsInline
                      className="h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute bottom-3 left-3 rounded-lg bg-black/60 px-3 py-1 text-sm text-white backdrop-blur-sm">
                    {isRemoteScreenSharing
                      ? `${remoteUserName}'s Screen`
                      : remoteUserName}
                  </div>
                  {isRemoteConnected && !isRemoteScreenSharing && (
                    <div className="absolute top-3 right-3 rounded-lg bg-red-500/90 px-2.5 py-1 text-xs font-semibold text-white">
                      Live
                    </div>
                  )}
                  <div className="absolute top-3 left-3 rounded-lg bg-black/60 px-2.5 py-1 text-xs text-white/80 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                    Click to expand
                  </div>
                </div>

                <div
                  className="group relative aspect-video cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-modal transition-all hover:border-amber-400/40"
                  onClick={() => setExpandedVideo("local")}
                >
                  {isRemoteScreenSharing ? (
                    <>
                      <video
                        ref={remoteVideoRef}
                        autoPlay
                        playsInline
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute bottom-3 left-3 rounded-lg bg-black/60 px-3 py-1 text-sm text-white backdrop-blur-sm">
                        {remoteUserName}
                      </div>
                      <div className="absolute top-3 right-3 rounded-lg bg-red-500/90 px-2.5 py-1 text-xs font-semibold text-white">
                        Live
                      </div>
                    </>
                  ) : (
                    <>
                      <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="mirror h-full w-full object-cover"
                      />
                      <div className="absolute bottom-3 left-3 rounded-lg bg-black/60 px-3 py-1 text-sm text-white backdrop-blur-sm">
                        You
                      </div>
                    </>
                  )}
                  <div className="absolute top-3 left-3 rounded-lg bg-black/60 px-2.5 py-1 text-xs text-white/80 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                    Click to expand
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Code Editor */}
          {showEditor && (
            <div className="mb-5 overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-modal">
              <CodeEditor
                roomId={roomId}
                isCodeEditorOpen={showEditor}
                role={userRole}
                editorControlledBy={editorControlledBy}
              />
            </div>
          )}

          {/* Controls */}
          <div className="mx-auto mb-4 flex w-fit max-w-full flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm sm:gap-2.5 sm:p-2.5">
            {controlButton(
              isVideoOn,
              "bg-white/10 text-white hover:bg-white/15",
              "bg-red-500/90 text-white hover:bg-red-500",
              toggleVideo,
              isVideoOn ? "Turn off camera" : "Turn on camera",
              isVideoOn ? (
                <Video className="h-5 w-5" />
              ) : (
                <VideoOff className="h-5 w-5" />
              ),
            )}

            {controlButton(
              isAudioOn,
              "bg-white/10 text-white hover:bg-white/15",
              "bg-red-500/90 text-white hover:bg-red-500",
              toggleAudio,
              isAudioOn ? "Mute microphone" : "Unmute microphone",
              isAudioOn ? (
                <Mic className="h-5 w-5" />
              ) : (
                <MicOff className="h-5 w-5" />
              ),
            )}

            {controlButton(
              showEditor,
              "bg-emerald-500/90 text-white hover:bg-emerald-500",
              "bg-white/10 text-white hover:bg-white/15",
              () => setShowEditor(!showEditor),
              showEditor ? "Hide code editor" : "Open code editor",
              <Code2 className="h-5 w-5" />,
            )}

            {userRole === "interviewer" && showEditor && (
              <button
                onClick={toggleEditorControl}
                title={`Editor controlled by ${
                  editorControlledBy === "candidate" ? "Candidate" : "You"
                }`}
                aria-label={`Editor controlled by ${
                  editorControlledBy === "candidate" ? "Candidate" : "You"
                }`}
                className={`flex h-11 items-center gap-2 rounded-xl px-3.5 text-sm font-semibold text-white transition-all duration-200 ${
                  editorControlledBy === "candidate"
                    ? "bg-purple-500/90 hover:bg-purple-500"
                    : "bg-orange-500/90 hover:bg-orange-500"
                }`}
              >
                <Users className="h-5 w-5" />
                <span className="hidden sm:inline">
                  {editorControlledBy === "candidate"
                    ? "Candidate's Turn"
                    : "Your Turn"}
                </span>
                <span className="sm:hidden">
                  {editorControlledBy === "candidate" ? "Candidate" : "You"}
                </span>
              </button>
            )}

            {controlButton(
              isScreenSharing,
              "bg-sky-500/90 text-white hover:bg-sky-500",
              "bg-white/10 text-white hover:bg-white/15",
              toggleScreenShare,
              isScreenSharing ? "Stop screen sharing" : "Share screen",
              isScreenSharing ? (
                <MonitorDown className="h-5 w-5" />
              ) : (
                <MonitorUp className="h-5 w-5" />
              ),
            )}

            {userRole === "interviewer" && (
              <button
                onClick={handleEndInterview}
                title="End interview"
                aria-label="End interview"
                className="ml-1 flex h-11 items-center gap-2 rounded-xl bg-red-500/90 px-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-red-500"
              >
                <PhoneOff className="h-5 w-5" />
                End
              </button>
            )}
          </div>
        </div>
      </div>

      {userRole === "interviewer" && <InterviewerNotesCard roomId={roomId} />}

      {/* Chat button */}
      <button
        onClick={() => setIsChatOpen(!isChatOpen)}
        className="fixed right-4 bottom-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400 text-cocoa-900 shadow-modal transition-all duration-200 hover:bg-amber-300 md:right-6 md:bottom-6"
        aria-label={isChatOpen ? "Close chat" : "Open chat"}
      >
        {isChatOpen ? (
          <X className="h-6 w-6" strokeWidth={2.25} />
        ) : (
          <MessageCircle className="h-6 w-6" strokeWidth={2.25} />
        )}
      </button>

      {isChatOpen && (
        <div className="fixed right-4 bottom-20 z-50 flex h-[500px] w-[380px] max-w-[calc(100vw-2rem)] animate-scale-in flex-col overflow-hidden rounded-2xl border border-white/10 bg-cocoa-800 shadow-modal md:right-6 md:bottom-24">
          <div className="flex items-center justify-between bg-cocoa-700 px-5 py-3.5">
            <h3 className="text-base font-semibold text-cream-100">Chat</h3>
            <button
              onClick={() => setIsChatOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-amber-200/70 transition-colors hover:bg-white/10 hover:text-amber-100"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-cocoa-900/40 p-4">
            {allMsgs.length === 0 ? (
              <p className="pt-10 text-center text-sm text-amber-100/40">
                No messages yet. Start the conversation!
              </p>
            ) : (
              allMsgs.map((cur_msg, idx) =>
                cur_msg.userId === userId ? (
                  <div key={idx} className="flex justify-end">
                    <div className="max-w-[75%] rounded-2xl rounded-br-md bg-amber-400 px-3.5 py-2 text-cocoa-900">
                      <p className="text-sm">{cur_msg.msg}</p>
                      <span className="mt-1 block text-[10px] text-cocoa-900/60">
                        {new Date(cur_msg.time).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div key={idx} className="flex justify-start">
                    <div className="max-w-[75%] rounded-2xl rounded-bl-md bg-white/10 px-3.5 py-2 text-cream-100">
                      <p className="text-sm">{cur_msg.msg}</p>
                      <span className="mt-1 block text-[10px] text-amber-100/40">
                        {new Date(cur_msg.time).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ),
              )
            )}
          </div>

          <div className="border-t border-white/10 bg-cocoa-700 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                onChange={handleOnChange}
                value={msg}
                placeholder="Type a message..."
                className="input h-11 py-0"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendCurrentMessage();
                  }
                }}
              />
              <button
                className="btn btn-primary btn-md shrink-0 px-4"
                onClick={sendCurrentMessage}
                disabled={!msg.trim()}
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {isScreenSharing && (
        <div className="fixed right-6 bottom-32 z-40 w-96 max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl border-2 border-sky-400/60 bg-gray-800 shadow-modal">
          <video
            ref={localScreenRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
          />
          <div className="absolute bottom-2 left-2 rounded-md bg-black/60 px-2.5 py-1 text-sm text-white backdrop-blur-sm">
            Your Screen
          </div>
          <div className="absolute top-2 right-2 rounded-md bg-sky-500/90 px-2 py-1 text-xs font-semibold text-white">
            Sharing
          </div>
        </div>
      )}

      {isRemoteScreenSharing && (
        <div className="fixed bottom-32 left-6 z-40 w-96 max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl border-2 border-purple-400/60 bg-gray-800 shadow-modal">
          <video
            ref={remoteScreenRef}
            autoPlay
            playsInline
            className="h-full w-full object-cover"
          />
          <div className="absolute top-2 left-2 h-24 w-32 overflow-hidden rounded-lg border-2 border-white/40 bg-black shadow-modal">
            <video
              ref={remoteCameraOverlayRef}
              autoPlay
              playsInline
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute bottom-2 left-2 rounded-md bg-black/60 px-2.5 py-1 text-sm text-white backdrop-blur-sm">
            Remote Screen
          </div>
          <div className="absolute top-2 right-2 rounded-md bg-purple-500/90 px-2 py-1 text-xs font-semibold text-white">
            Sharing
          </div>
        </div>
      )}
    </>
  );
}
